/**
 * ForwardToTeamChatModal — Inoltra uno o più messaggi WhatsApp a una chat del team
 * Notion-style design, basato su ShareToChatModal
 */

import React, { useState, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, Users, Send, MessageSquare } from 'lucide-react';
import { useTeamChatStore } from '@/lib/stores/teamChatStore';
import { useAuth } from '@/lib/auth';
import { Message } from '@/lib/types/whatsappChat';
import toast from 'react-hot-toast';

interface ForwardToTeamChatModalProps {
  messages: Message[];
  chatName: string;
  chatId: string;
  onClose: () => void;
}

const MAX_PREVIEW = 3;

export function ForwardToTeamChatModal({ messages, chatName, chatId, onClose }: ForwardToTeamChatModalProps) {
  const { userProfile } = useAuth();
  const { chats, sendMessage, selectChat, setWidgetState } = useTeamChatStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);

  const getChatDisplayName = useCallback((c: typeof chats[0]) => {
    if (c.is_group && c.name) return c.name;
    const other = c.participants?.find(p => p.user_id !== userProfile?.id);
    return other?.user?.full_name || 'Chat';
  }, [userProfile?.id]);

  const filteredChats = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    return chats.filter(c => !q || getChatDisplayName(c).toLowerCase().includes(q));
  }, [chats, searchQuery, getChatDisplayName]);

  const handleSend = useCallback(async () => {
    if (!selectedChatId) return;
    setSending(true);
    try {
      const payload = JSON.stringify({
        messages: messages.map(m => ({
          id: m.id,
          content: m.content,
          type: m.type,
          direction: m.direction,
          senderName: m.senderName || chatName,
          senderJid: m.senderJid || m.chatId,
          timestamp: m.timestamp instanceof Date ? m.timestamp.toISOString() : m.timestamp,
          mediaUrl: m.mediaUrl,
          mediaType: m.mediaMimetype,
        })),
        chatName,
        chatId,
        note: note.trim() || null,
      });
      await sendMessage(selectedChatId, payload, null, undefined, 'whatsapp_forward');
      toast.success('Messaggi inoltrati in Team Chat');
      onClose();
      await selectChat(selectedChatId);
      setWidgetState('expanded');
    } catch {
      toast.error("Errore nell'inoltro");
    } finally {
      setSending(false);
    }
  }, [selectedChatId, messages, chatName, chatId, note, sendMessage, onClose, selectChat, setWidgetState]);

  const getInitials = (name: string) =>
    name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  const previewMessages = messages.slice(0, MAX_PREVIEW);
  const remaining = messages.length - MAX_PREVIEW;

  const getMessagePreview = (m: Message) => {
    if (m.type === 'image') return '📷 Immagine';
    if (m.type === 'video') return '🎥 Video';
    if (m.type === 'audio' || m.type === 'ptt') return '🎵 Audio';
    if (m.type === 'document') return '📄 Documento';
    if (m.type === 'sticker') return '🏷️ Sticker';
    if (m.type === 'location') return '📍 Posizione';
    if (m.type === 'contact') return '👤 Contatto';
    return m.content || '';
  };

  return createPortal(
    <>
      <div className="fixed inset-0 z-[9998] bg-black/40" onClick={onClose} />
      <div
        className="fixed z-[9999] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 max-h-[80vh] bg-white dark:bg-[#2F3037] rounded-xl flex flex-col"
        style={{ border: '1px solid rgba(55,53,47,0.09)', boxShadow: 'rgba(55,53,47,0.1) 0px 8px 32px' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: '1px solid rgba(55,53,47,0.09)' }}>
          <div className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4" style={{ color: '#25D366' }} strokeWidth={1.5} />
            <span className="font-semibold text-sm" style={{ color: '#37352F' }}>Inoltra a Team Chat</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[11px] px-2 py-0.5 rounded-full" style={{ color: '#787774', backgroundColor: 'rgba(55,53,47,0.06)' }}>
              {messages.length} {messages.length === 1 ? 'messaggio' : 'messaggi'}
            </span>
            <button onClick={onClose} className="p-1 rounded hover:bg-[rgba(55,53,47,0.06)]">
              <X className="w-4 h-4" style={{ color: '#787774' }} strokeWidth={1.5} />
            </button>
          </div>
        </div>

        {/* WA Messages Preview */}
        <div className="px-4 py-2.5" style={{ backgroundColor: 'rgba(37,211,102,0.06)', borderBottom: '1px solid rgba(55,53,47,0.06)' }}>
          <div className="flex items-center gap-1.5 mb-2">
            <svg viewBox="0 0 24 24" className="w-3 h-3 flex-shrink-0" style={{ fill: '#25D366' }}>
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
              <path d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.832-1.438A9.955 9.955 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2zm0 18a8 8 0 01-4.243-1.214l-.257-.154-2.786.828.828-2.786-.154-.257A8 8 0 1112 20z"/>
            </svg>
            <span className="text-[11px] font-medium" style={{ color: '#25D366' }}>{chatName}</span>
          </div>
          <div className="space-y-1">
            {previewMessages.map((m) => (
              <div key={m.id} className="flex items-baseline gap-2">
                <span className="text-[11px] font-medium flex-shrink-0" style={{ color: m.direction === 'outbound' ? '#2383E2' : '#25D366' }}>
                  {m.direction === 'outbound' ? 'Tu' : (m.senderName || chatName)}
                </span>
                <span className="text-[11px] truncate" style={{ color: '#555' }}>
                  {getMessagePreview(m)}
                </span>
              </div>
            ))}
            {remaining > 0 && (
              <div className="text-[10px]" style={{ color: '#9B9A97' }}>
                +{remaining} {remaining === 1 ? 'altro messaggio' : 'altri messaggi'}
              </div>
            )}
          </div>
        </div>

        {/* Note */}
        <div className="px-4 py-2" style={{ borderBottom: '1px solid rgba(55,53,47,0.06)' }}>
          <input
            type="text"
            placeholder="💬 Aggiungi una nota... (opzionale)"
            value={note}
            onChange={e => setNote(e.target.value)}
            className="w-full text-sm rounded-md outline-none px-0 py-1 bg-transparent dark:text-white"
            style={{ color: '#37352F', fontSize: '13px' }}
          />
        </div>

        {/* Search */}
        <div className="px-3 py-2" style={{ borderBottom: '1px solid rgba(55,53,47,0.06)' }}>
          <div className="relative flex items-center">
            <Search className="absolute left-2.5 w-3.5 h-3.5 pointer-events-none" style={{ color: '#9B9A97' }} strokeWidth={1.5} />
            <input
              type="text"
              placeholder="Cerca chat o persona..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-sm rounded-md outline-none"
              style={{ backgroundColor: 'rgba(55,53,47,0.06)', color: '#37352F', fontSize: '13px', border: '1px solid transparent' }}
              autoFocus
            />
          </div>
        </div>

        {/* Chat list */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {filteredChats.length === 0 ? (
            <p className="text-xs px-4 py-6 text-center" style={{ color: '#9B9A97' }}>Nessuna chat</p>
          ) : (
            filteredChats.map(c => {
              const name = getChatDisplayName(c);
              const isSelected = c.id === selectedChatId;
              return (
                <button
                  key={c.id}
                  onClick={() => setSelectedChatId(c.id)}
                  className="w-full flex items-center gap-2.5 px-4 py-2.5 text-left transition-colors"
                  style={{ backgroundColor: isSelected ? 'rgba(35,131,226,0.1)' : 'transparent' }}
                  onMouseEnter={e => !isSelected && (e.currentTarget.style.backgroundColor = 'rgba(55,53,47,0.04)')}
                  onMouseLeave={e => !isSelected && (e.currentTarget.style.backgroundColor = 'transparent')}
                >
                  {c.is_group ? (
                    <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(35,131,226,0.1)' }}>
                      <Users className="w-4 h-4" style={{ color: '#2383E2' }} strokeWidth={1.5} />
                    </div>
                  ) : (
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium" style={{ backgroundColor: 'rgba(55,53,47,0.08)', color: '#37352F' }}>
                      {getInitials(name)}
                    </div>
                  )}
                  <span className="text-[13px] truncate" style={{ color: isSelected ? '#2383E2' : '#37352F', fontWeight: isSelected ? 600 : 500 }}>
                    {name}
                  </span>
                </button>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 flex items-center justify-between" style={{ borderTop: '1px solid rgba(55,53,47,0.09)' }}>
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg text-sm font-medium transition-colors hover:bg-[rgba(55,53,47,0.06)]"
            style={{ color: '#787774' }}
          >
            Annulla
          </button>
          <button
            onClick={handleSend}
            disabled={!selectedChatId || sending}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors disabled:opacity-50"
            style={{ backgroundColor: '#25D366' }}
          >
            <Send className="w-4 h-4" strokeWidth={1.5} />
            {sending ? 'Invio...' : 'Inoltra'}
          </button>
        </div>
      </div>
    </>,
    document.body
  );
}
